/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useCallback, useRef } from 'react';
import AsideWrapper from '@fe-news/components/Aside/AsideWrapper';
import { styled } from '@linaria/react';
import useFetchHotNews from './use-fetch-hot-news';
import { GTMEventKey, CustomEventName, sendGTM } from '@fe-cnyes/fe-common-gtm';
import { useIsInteractingObserver } from '@fe-common-utils/libs/hooks/useIsInteractingObserver';
import Image from 'next/image';
import HotNewsLabelIcon from '@fe-news/public/assets/icons/hot/hot-news-fire.svg';
import HotNewsIcon from '@fe-news/public/assets/icons/anue-hot-news.svg';

const GTM_EVENT_BUTTON_CLICK = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_BUTTON,
  [GTMEventKey.SECTION]: 'News_Timed_Popup_熱門新聞',
  [GTMEventKey.CLICK_ITEM]: '看更多'
};

const GTM_EVENT_IMP_PLACEMENT = {
  [GTMEventKey.EVENT_NAME]: CustomEventName.IMP_PLACEMENT,
  [GTMEventKey.SECTION]: 'News_Timed_Popup_熱門新聞'
};

const OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 1
};

const HotNewsWrapper = styled.div`
  width: calc(100% - 334px);

  :global(#modalIssueNews) {
    h3 {
      padding-left: 22px;
    }
  }
`;

const HotNewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 6px;
  border: solid 1px #e2e8f1;
  background-color: #fff;
  gap: 14px;
  margin-top: 12px;

  &:not(:last-child) {
    padding-bottom: 12px;
  }
`;

const HotNewsItem = styled.a`
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:not(:last-of-type) {
    padding-bottom: 12px;
    border-bottom: 1px solid #e2e8f1;
  }
`;

const HotNewsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  p:not(:last-child) {
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      top: 50%;
      right: -12px;
      transform: translateY(-50%);
      width: 4px;
      height: 4px;
      background-color: #e2e8f1;
      border-radius: 2px;
    }
  }

  .time {
    font-size: 13px;
    line-height: 13px;
    letter-spacing: 0.54px;
    color: #848d97;
  }

  .category {
    font-size: 13px;
    line-height: 13px;
    letter-spacing: 0.54px;
    color: #515c69;
  }
`;

const HotNewsContent = styled.div`
  display: flex;
  align-items: center;

  img {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }

  p {
    font-size: 16px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.5px;
    color: #383838;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover {
      color: #e03f19;
    }
  }
`;

const onClick = (id: number, title: string) => {
  sendGTM({
    [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
    [GTMEventKey.SECTION]: 'News_Timed_Popup_熱門新聞',
    [GTMEventKey.CLICK_ITEM]: `${id}_${title}`
  });
};

const HotNews = () => {
  const { data } = useFetchHotNews();
  const countHotNewsFirstEnter = useRef(0);

  const onEnterHotNews = useCallback(() => {
    if (!countHotNewsFirstEnter.current) {
      countHotNewsFirstEnter.current = 1;
      sendGTM(GTM_EVENT_IMP_PLACEMENT);
    }
  }, []);
  const hotNewsReference = useIsInteractingObserver(onEnterHotNews, OBSERVER_OPTIONS);
  return (
    <HotNewsWrapper ref={hotNewsReference}>
      <AsideWrapper
        title='熱門新聞'
        id='modalIssueNews'
        icon={HotNewsIcon}
        gtmEvent={GTM_EVENT_BUTTON_CLICK}
        readMoreLink='/news/cat/headline'
      >
        <HotNewsContainer>
          {data?.map(({ newsId, title, category, time }: any, index) => (
            <HotNewsItem
              role='list'
              href={`/news/id/${newsId}`}
              key={`${newsId}-${index}`}
              onClick={() => onClick(newsId, title)}
            >
              <HotNewsTitle>
                <p className='time'>{time}</p>
                <p className='category'>{category}</p>
              </HotNewsTitle>
              <HotNewsContent>
                <Image src={HotNewsLabelIcon} alt='label-icon' />
                <p>{title}</p>
              </HotNewsContent>
            </HotNewsItem>
          ))}
        </HotNewsContainer>
      </AsideWrapper>
    </HotNewsWrapper>
  );
};

export default HotNews;
